import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { CalendarIcon } from '../../../../../../components/icons/CalendarIcon'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../../../../../../components/ui/tooltip'
import { addMonths, monthKey } from '../../hooks/upcomingTasks.utils'
import type { MonthSelectorProps } from './MonthSelector.types'
import './MonthSelector.scss'

/**
 * Short-month label via Intl (e.g. "Apr", "Sept" in DE; "Apr", "Sep" in EN).
 * Trims trailing "." that some locales append.
 */
function shortMonthName(date: Date, locale: string): string {
  return date
    .toLocaleDateString(locale, { month: 'short' })
    .replace(/\.$/, '')
}

// Press-and-hold accelerator constants for the arrow buttons.
const HOLD_START_DELAY_MS = 350
const HOLD_REPEAT_MS = 80
// How far each arrow click scrolls the strip (in px). Roughly one chip.
const ARROW_SCROLL_STEP_PX = 60

export const MonthSelector: React.FC<MonthSelectorProps> = ({
  selectedMonth,
  todayMonth,
  taskCountsByMonth,
  rangeMonths = 60,
  onSelect,
  onJumpToday,
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : 'en-US'

  // Full chip list — symmetric around today. ±60 months (5y) by default.
  const months = useMemo(() => {
    const first = addMonths(todayMonth, -rangeMonths)
    return Array.from({ length: rangeMonths * 2 + 1 }, (_, i) => addMonths(first, i))
  }, [todayMonth, rangeMonths])

  const selectedKey = monthKey(selectedMonth)
  const todayKey = monthKey(todayMonth)

  // ── Scrollable chip strip ──
  const stripRef = useRef<HTMLDivElement>(null)
  const chipRefs = useRef(new Map<string, HTMLButtonElement | null>())
  const registerChip = useCallback((key: string, el: HTMLButtonElement | null) => {
    if (el) chipRefs.current.set(key, el)
    else chipRefs.current.delete(key)
  }, [])

  // Scroll so that `selectedMonth` is visible. Runs on mount and whenever
  // the selection changes externally (chip click, keyboard shortcut, scroll-
  // driven visible-month sync).
  const didMountRef = useRef(false)
  useLayoutEffect(() => {
    const strip = stripRef.current
    const chip = chipRefs.current.get(selectedKey)
    if (!strip || !chip) return
    const stripRect = strip.getBoundingClientRect()
    const chipRect = chip.getBoundingClientRect()
    // Already visible (with a tiny margin) → skip.
    const margin = 16
    if (chipRect.left >= stripRect.left + margin && chipRect.right <= stripRect.right - margin) {
      return
    }
    // Center the chip within the strip viewport.
    const delta = chipRect.left - stripRect.left - (stripRect.width - chipRect.width) / 2
    const targetLeft = strip.scrollLeft + delta
    if (typeof strip.scrollTo === 'function') {
      strip.scrollTo({ left: targetLeft, behavior: didMountRef.current ? 'smooth' : 'auto' })
    } else {
      strip.scrollLeft = targetLeft
    }
    didMountRef.current = true
  }, [selectedKey])

  // ── Arrow buttons: scroll strip by one chip-width. ──
  const scrollByStep = useCallback((direction: -1 | 1) => {
    const strip = stripRef.current
    if (!strip) return
    if (typeof strip.scrollBy === 'function') {
      strip.scrollBy({ left: direction * ARROW_SCROLL_STEP_PX, behavior: 'smooth' })
    } else {
      strip.scrollLeft += direction * ARROW_SCROLL_STEP_PX
    }
  }, [])

  // ── Press-and-hold accelerator ──
  const holdTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const holdIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const clearHold = useCallback(() => {
    if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current)
    if (holdIntervalRef.current) clearInterval(holdIntervalRef.current)
    holdTimeoutRef.current = null
    holdIntervalRef.current = null
  }, [])
  const startHold = useCallback((direction: -1 | 1) => {
    holdTimeoutRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => scrollByStep(direction), HOLD_REPEAT_MS)
    }, HOLD_START_DELAY_MS)
  }, [scrollByStep])
  useEffect(() => () => clearHold(), [clearHold])

  // Browsers that receive vertical wheel deltas over a horizontally scrollable
  // element need a nudge to translate them into horizontal scroll.
  const onWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return // trackpad horizontal → native
    const strip = stripRef.current
    if (!strip) return
    strip.scrollLeft += e.deltaY
    e.preventDefault()
  }, [])

  // ── Click-and-drag scrolling ──
  // Mouse users can grab the strip and pull it left/right (touch already uses
  // native scroll). A movement threshold distinguishes "drag" from "click" so
  // chip clicks still work when the user doesn't actually pull.
  const [isDragging, setIsDragging] = useState(false)
  const dragStartXRef = useRef(0)
  const dragStartScrollRef = useRef(0)
  const dragMovedRef = useRef(false)
  const activePointerIdRef = useRef<number | null>(null)

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Only left mouse button; touch uses native scroll momentum.
    if (e.pointerType === 'touch') return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    const strip = stripRef.current
    if (!strip) return
    dragStartXRef.current = e.clientX
    dragStartScrollRef.current = strip.scrollLeft
    dragMovedRef.current = false
    activePointerIdRef.current = e.pointerId
    // We intentionally do NOT call setPointerCapture here — doing so would
    // make the strip itself the click target and swallow chip clicks. We
    // only capture once the user has actually dragged (see below).
  }, [])

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (activePointerIdRef.current !== e.pointerId) return
    const strip = stripRef.current
    if (!strip) return
    const dx = e.clientX - dragStartXRef.current
    if (!dragMovedRef.current && Math.abs(dx) > 3) {
      dragMovedRef.current = true
      setIsDragging(true)
      // Now that a real drag is underway, capture further pointer events so
      // the drag keeps tracking even if the cursor leaves the strip.
      try { strip.setPointerCapture(e.pointerId) } catch { /* noop */ }
    }
    if (dragMovedRef.current) {
      strip.scrollLeft = dragStartScrollRef.current - dx
    }
  }, [])

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (activePointerIdRef.current !== e.pointerId) return
    activePointerIdRef.current = null
    if (isDragging) setIsDragging(false)
    const strip = stripRef.current
    if (strip && strip.hasPointerCapture?.(e.pointerId)) {
      try { strip.releasePointerCapture(e.pointerId) } catch { /* noop */ }
    }
  }, [isDragging])

  // Cancel a chip click if the user actually dragged. Must run in the capture
  // phase so it fires before the chip's own onClick.
  const onClickCapture = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (dragMovedRef.current) {
      e.preventDefault()
      e.stopPropagation()
      dragMovedRef.current = false
    }
  }, [])

  return (
    <div
      className="month-selector"
      role="tablist"
      aria-label={t('upcoming.monthSelector.prev') + ' / ' + t('upcoming.monthSelector.next')}
    >
      {onJumpToday && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="month-selector__today"
                onClick={(e) => {
                  onJumpToday()
                  // Drop focus immediately so the Radix tooltip doesn't
                  // re-open on window refocus after the user Alt-Tabs away
                  // and back. Keyboard users can still Tab back to the
                  // button if they want.
                  e.currentTarget.blur()
                }}
                aria-label={t('upcoming.monthSelector.today')}
              >
                <CalendarIcon size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              {t('upcoming.monthSelector.today')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <button
        type="button"
        className="month-selector__arrow"
        onClick={() => scrollByStep(-1)}
        onPointerDown={() => startHold(-1)}
        onPointerUp={clearHold}
        onPointerLeave={clearHold}
        onPointerCancel={clearHold}
        aria-label={t('upcoming.monthSelector.prev')}
      >
        <ChevronLeft size={14} strokeWidth={2.5} />
      </button>

      <div
        className={`month-selector__chips${isDragging ? ' month-selector__chips--dragging' : ''}`}
        ref={stripRef}
        role="none"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={onClickCapture}
      >
        {months.map((month, i) => {
          const key = monthKey(month)
          const isSelected = key === selectedKey
          const isToday = key === todayKey
          const isYearBreak = month.getMonth() === 0 && i > 0
          const count = taskCountsByMonth?.[key] ?? 0
          return (
            <React.Fragment key={key}>
              {isYearBreak && <span className="month-selector__year-break" aria-hidden="true" />}
              <button
                ref={el => registerChip(key, el)}
                type="button"
                role="tab"
                aria-selected={isSelected}
                aria-current={isToday ? 'date' : undefined}
                className={[
                  'month-selector__chip',
                  isSelected && 'month-selector__chip--selected',
                  isToday && 'month-selector__chip--today',
                ].filter(Boolean).join(' ')}
                onClick={() => onSelect(month)}
              >
                <span className="month-selector__chip-label">
                  {shortMonthName(month, locale)}
                  {month.getMonth() === 0 && (
                    <>
                      {' '}
                      <span className="month-selector__chip-label-year">{month.getFullYear()}</span>
                    </>
                  )}
                </span>
                {count > 0 && (
                  <span className="month-selector__chip-count" aria-label={`${count} Aufgaben`}>
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </button>
            </React.Fragment>
          )
        })}
      </div>

      <button
        type="button"
        className="month-selector__arrow"
        onClick={() => scrollByStep(1)}
        onPointerDown={() => startHold(1)}
        onPointerUp={clearHold}
        onPointerLeave={clearHold}
        onPointerCancel={clearHold}
        aria-label={t('upcoming.monthSelector.next')}
      >
        <ChevronRight size={14} strokeWidth={2.5} />
      </button>
    </div>
  )
}
